import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, Input, Field } from '@alifd/next';
import $i18n from '@/i18n';
import { Select, Radio, Icon, Form, Checkbox } from '@b-design/fusion';
import Requests from '@/services/request';
import DefaultAppIcon from '@/components/DefaultAppIcon';
import { HasAigcRole, HasOperatorRole } from '@/utils/config';
import { FormSubmit } from '@/components/Button';

interface AppsItem {
  appId: 'string';
  createName: 'string';
  createUid: 'string';
  description: {
    createName: 'string';
    createUid: 'string';
    customerEmail: 'string';
    customerPhone: 'string';
    description: 'string';
    gmtCreate: '2022-12-21T09:17:51.064Z';
    gmtModified: '2022-12-21T09:17:51.064Z';
    icon: 'string';
    iconPublicUrl: 'string';
    inherit: boolean;
    modifiedName: 'string';
    modifiedUid: 'string';
    name: 'string';
    slogan: 'string';
  };
  devConfig: {};
  devPlatform: 'string';
  extMap: {};
  feature: {};
  framework: 'string';
  frameworkDesc: 'string';
  gmtCreate: '2022-12-21T09:17:51.064Z';
  gmtModified: '2022-12-21T09:17:51.064Z';
  maxVersion: 'string';
  modifiedName: 'string';
  modifiedUid: 'string';
  platform: 'string';
  platformDesc: 'string';
  roleDesc: 'string';
  status: 'string';
  statusDesc: 'string';
}

interface AddMemberModal {
  addMemberVisible: boolean;
  onAddMemberClose: () => void;
  onFinish: (values: any, error: any[]) => Promise<void>;
  spaceId: string | (string | null)[] | null;
  roleList: string[];
}

// assign app的下拉样式自定义
const selectItemRender = (item) => {
  return (
    <span>
      {item?.description?.iconPublicUrl ? (
        <img src={item?.description?.iconPublicUrl} style={{ width: 16, marginRight: 8 }} alt="" />
      ) : (
        <DefaultAppIcon style={{ width: 16, marginRight: 8 }} />
      )}

      <span className="common-table-app-name">{item.description.name}</span>
    </span>
  );
};

const AddMemberModal: React.FC<AddMemberModal> = ({
  addMemberVisible,
  onAddMemberClose,
  onFinish,
  spaceId,
  roleList,
}) => {
  // 所有下拉app
  const [apps, setApps] = useState<AppsItem[]>([]);
  // 搜索value
  const [searchValue, setSearchValue] = useState<string | undefined>();

  const field = Field.useField();
  const { reset } = field;

  const onlyAigc = roleList?.includes('AIGC_DESIGNER') && !roleList?.includes('ADMIN');

  // 获取所有app list
  const queryApp = () => {
    if (!spaceId) {
      return;
    };
    Requests.get('/rest/intercnn/app/query', {
      spaceId,
      currentPage: 1,
      pageSize: 10000,
      name: searchValue || undefined,
    }).then((data) => {
      if (data) {
        setApps(
          data?.list?.map((app) => ({ ...app, value: app?.appId, label: app.description.name })),
        );
      }
    });
  };

  // 获取对应空间下的app
  useEffect(() => {
    if (addMemberVisible) {
      queryApp();
    }
  }, [addMemberVisible]);

  const radioOnChange = useCallback((value) => {
    if (!value?.includes('DEVELOPER')) {
      reset(['appList']);
    }
  }, []);

  // assign app搜索input自定义
  const menuProps = {
    header: (
      <div style={{ padding: '0 4px', textAlign: 'center' }}>
        <div style={{ paddingBottom: 4 }}>
          <Input
            innerAfter={<Icon type="search" style={{ marginTop: 3 }} />}
            placeholder={$i18n.get({ id: 'aopf.pages.miniapp.input.Search', dm: '搜索...' })}
            style={{ width: '100%' }}
            value={searchValue}
            onChange={(v) => setSearchValue(v)}
            onPressEnter={queryApp}
          />
        </div>
      </div>
    ),
  };

  return (
    <div>
      <Dialog
        v2
        title={$i18n.get({ id: 'aopf.pages.member.addMembers', dm: '添加成员' })}
        visible={addMemberVisible}
        onClose={() => {
          onAddMemberClose();
          setSearchValue(undefined);
        }}
        footer={false}
        width={818}
        className="modal-addMember"
      >
        <Form
          labelCol={{
            span: 8,
          }}
          wrapperCol={{
            span: 16,
          }}
          labelAlign="left"
          labelTextAlign="left"
          field={field}
        >
          <Form.Item
            label={$i18n.get({ id: 'aopf.pages.member.addMemberName', dm: '名称' })}
            name="invitee"
            required
            requiredMessage={$i18n.get({
              id: 'aopf.pages.member.addMemberNameErrorTips',
              dm: '请输入名称',
            })}
            minLength={2}
            maxLength={36}
            minmaxLengthMessage={$i18n.get({
              id: 'aopf.pages.member.addMemberNameErrorTips.rules',
              dm: '输入具有2-36个字符的名称!',
            })}
          >
            <Input
              placeholder={$i18n.get({
                id: 'aopf.pages.member.addMemberNamePlacehold',
                dm: '输入具有2-36个字符的唯一名称。',
              })}
              style={{ width: '470px' }}
              maxLength={36}
            />
          </Form.Item>

          <Form.Item
            label={$i18n.get({ id: 'aopf.pages.member.addMemberEmail', dm: '邮箱' })}
            name="inviteEmail"
            required
            requiredMessage={$i18n.get({
              id: 'aopf.pages.member.addMemberEmailErrorTip',
              dm: '请输入邮箱',
            })}
            validator={(rule, value) => {
              return new Promise((resolve, reject) => {
                const regex = /^[A-Za-z\d]+([-_.][A-Za-z\d]+)*@([A-Za-z\d]+[-.])+[A-Za-z\d]{2,}$/;
                if (!regex.test(value)) {
                  return reject(
                    $i18n.get({
                      id: 'aopf.pages.member.addMemberEmailErrorTips',
                      dm: '输入的电子邮件无效',
                    }),
                  );
                } else {
                  resolve(null);
                }
              });
            }}
          >
            <Input
              style={{ width: '470px' }}
              placeholder={$i18n.get({
                id: 'aopf.pages.member.addMemberEmail.placeholder',
                dm: 'example@email.com',
              })}
            />
          </Form.Item>

          <Form.Item
            label={$i18n.get({ id: 'aopf.pages.member.columnsRole', dm: '角色' })}
            name="roleList"
            required
            requiredMessage={$i18n.get({
              id: 'aopf.pages.member.addMemberRoleErrorTip',
              dm: '请选择角色',
            })}
          >
            <Checkbox.Group direction="ver" onChange={radioOnChange}>
              <Checkbox
                value="DEVELOPER"
                style={{
                  boxSizing: 'border-box',
                  width: 465,
                  backgroundColor: '#FAFAFC',
                  borderRadius: 4,
                  paddingTop: 16,
                  paddingLeft: 16,
                }}
                disabled={onlyAigc}
              >
                <span className="roleTitle">
                  {$i18n.get({ id: 'aopf.pages.member.actionSetAsDeveloper', dm: '开发者' })}
                </span>
                <span className="roleDesc">
                  {$i18n.get({
                    id: 'aopf.pages.member.actionSetAsDeveloperDesc',
                    dm: '开发与测试应用',
                  })}
                </span>
                <Form.Item
                  name="appList"
                  label={$i18n.get({
                    id: 'aopf.pages.member.addMemberAssignApps',
                    dm: '分派应用',
                  })}
                  labelCol={{
                    span: 6,
                  }}
                  wrapperCol={{
                    span: 18,
                  }}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder={$i18n.get({
                      id: 'aopf.pages.member.addMemberAssignAppsPlacehold',
                      dm: '选择应用程序',
                    })}
                    hasClear
                    mode="multiple"
                    showSearch={true}
                    style={{ minWidth: '300px' }}
                    itemRender={selectItemRender}
                    valueRender={selectItemRender}
                    dataSource={apps}
                    menuProps={menuProps}
                    popupAutoFocus
                    popupProps={{ v2: true }}
                    popupClassName="b-design-select"
                    disabled={onlyAigc}
                  />
                </Form.Item>
              </Checkbox>
              <Checkbox
                value="ADMIN"
                style={{
                  boxSizing: 'border-box',
                  width: 465,
                  backgroundColor: '#FAFAFC',
                  borderRadius: 4,
                  padding: 16,
                }}
                disabled={onlyAigc}
              >
                <span className="roleTitle">
                  {$i18n.get({ id: 'aopf.pages.member.actionSetAsAdmin', dm: '管理员' })}
                </span>
                <span className="roleDesc">
                  {$i18n.get({
                    id: 'aopf.pages.member.actionSetAsAdminDesc',
                    dm: '管理所有应用程序的最高权限',
                  })}
                </span>
              </Checkbox>
              {HasOperatorRole() && <Checkbox
                value="OPERATOR"
                style={{
                  boxSizing: 'border-box',
                  width: 465,
                  backgroundColor: '#FAFAFC',
                  borderRadius: 4,
                  padding: 16,
                }}
              >
                <span className="roleTitle">
                  {$i18n.get({
                    id: 'aopf.component.addMemberModal.indexCp.OperationsAdministrator',
                    dm: '运营管理员',
                  })}
                </span>
                <span className="roleDesc">
                  {$i18n.get({
                    id: 'aopf.component.addMemberModal.indexCp.HaveManagementPermissionsForMini',
                    dm: '拥有小程序运营的管理权限',
                  })}
                </span>
              </Checkbox>}
              {HasAigcRole() && (
                <Checkbox
                  value="AIGC_DESIGNER"
                  style={{
                    boxSizing: 'border-box',
                    width: 465,
                    backgroundColor: '#FAFAFC',
                    borderRadius: 4,
                    padding: 16,
                  }}
                  disabled={!roleList.includes('AIGC_DESIGNER')}
                >
                  <span className="roleTitle">
                    {$i18n.get({
                      id: 'aopf.component.addMemberModal.indexCp.AigcDesigner',
                      dm: 'AIGC设计者',
                    })}
                  </span>
                  <span className="roleDesc">
                    {$i18n.get({
                      id: 'aopf.component.addMemberModal.indexCp.PermissionToUseTheSmart',
                      dm: '智绘平台使用权限',
                    })}
                  </span>
                </Checkbox>
              )}
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            label={$i18n.get({
              id: 'aopf.pages.member.addMemberInvitationValidity',
              dm: '邀请有效期',
            })}
            name="validDate"
            required
            requiredMessage={$i18n.get({
              id: 'aopf.pages.member.addMemberInvitationValidityErrorTip',
              dm: '请选择邀请有效期',
            })}
          >
            <Radio.Group defaultValue={1}>
              <Radio value={1}>
                <span className="roleTitle">
                  {$i18n.get({
                    id: 'aopf.pages.member.addMemberInvitationValidityItem',
                    dm: '24小时',
                  })}
                </span>
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item label=" " labelCol={{ span: 10 }} style={{ textAlign: 'right' }}>
            <Form.Reset
              onClick={() => {
                onAddMemberClose();
              }}
            >
              {$i18n.get({
                id: 'aopf.pages.addMember.btn.cancel',
                dm: '取消',
              })}
            </Form.Reset>
            <FormSubmit validate type="primary" onClick={onFinish} style={{ margin: '0 10px' }}>
              {$i18n.get({
                id: 'aopf.pages.addMember.btn.ok',
                dm: '确定',
              })}
            </FormSubmit>
          </Form.Item>
        </Form>
      </Dialog>
    </div>
  );
};
export default AddMemberModal;
